import React, { Fragment } from 'react';
import cx from 'classnames';

import type { CartItemType } from '@zola-helpers/client/dist/es/@types/cart';
import { compactCurrencyFilter } from '../utils/PricingUtils';
import { LinkV2 } from '../../LinkV2';

import { CartItemPrice } from './CartItemPrice';
import { CartItemAttributes } from './CartItemAttributes';
import { GroupGiftTag } from './GroupGiftTag';
import { isDigitalGreetingCard } from '../utils/digitalGreetingCards';
import { CartItemQuantityInput } from './CartItemQuantityInput';
import { CartItemDetails } from './CartItemDetails';
import { CartItemName, CartItemNameLink, CartItemNamePriceWrapper } from './CartItemName';
import { CartItemQuantity } from './CartItemQuantity';
import { CartItemImage } from './CartItemImage';
import { CartItemSubTotal } from './CartItemSubTotal';

import styles from './cartItem.module.less';
import mixinStyles from '../stylesheets/mixins.module.less';
import cartContainerStyles from '../stylesheets/cartContainer.module.less';
import { CartItemDiscount } from './CartItemDiscount';

type CartItemProps = {
  item: CartItemType;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  disableCartItemLink?: boolean;
  remove: (itemId: string) => Promise<void>;
  isDgcV5: boolean;
};

export const CartItem = ({
  item,
  disableCartItemLink = false,
  isDgcV5,
  ...rest
}: CartItemProps): JSX.Element | null => {
  const isPdpLinkShown = item.showLinkToPdp && !disableCartItemLink;

  const itemName = isDigitalGreetingCard(item) ? 'Personalized e-card' : item.name;

  if (isDigitalGreetingCard(item) && isDgcV5) return null;

  const isSupplyChainFee = false; // Disabled in prod currently so keeping as false

  return (
    <Fragment>
      <CartItemImage>
        {item.itemLink && isPdpLinkShown ? (
          <LinkV2 href={item.itemLink} target="_self">
            <img
              className={styles.itemImage}
              src={item.imageLink || undefined}
              alt={itemName || undefined}
            />
          </LinkV2>
        ) : (
          <img
            className={styles.itemImage}
            src={item.imageLink || undefined}
            alt={itemName || undefined}
          />
        )}
      </CartItemImage>

      <CartItemDetails>
        <CartItemNamePriceWrapper>
          {item.itemLink && isPdpLinkShown ? (
            <CartItemNameLink href={item.itemLink} target="_self" data-testid="cart-item-name">
              {itemName}
            </CartItemNameLink>
          ) : (
            <CartItemName data-testid="cart-item-name">{itemName}</CartItemName>
          )}

          <CartItemSubTotal item={item} className={cartContainerStyles.visibleContainerSmall} />
        </CartItemNamePriceWrapper>

        {/* This could be a component: its all the attributes */}
        <Fragment>
          {item.groupGift && <GroupGiftTag />}

          <CartItemAttributes item={item} />

          {item.showExternalWarning && (
            <div className={styles.cartItemNote}>
              Please remember that this item cannot be shipped to you and is redeemable by the
              couple at their convenience.
            </div>
          )}
          {item.isUpdated && (
            <div className={styles.cartItemNote}>
              We have a limited quantity of this item. We&apos;ve adjusted your quantity above to
              reflect the maximum number available.
            </div>
          )}
          {(item.deliverySurcharge || 0) > 0 &&
            (isSupplyChainFee ? item.deliverySurchargeCents !== 199 : true) && (
              <div className={styles.cartItemNote}>
                Due to its weight or perishable nature, this item has an incremental delivery
                surcharge of {compactCurrencyFilter(item.deliverySurcharge)}.
              </div>
            )}
          {item.cartItemMessage && <CartItemDiscount>{item.cartItemMessage}</CartItemDiscount>}
        </Fragment>

        <div
          className={cx(cartContainerStyles.visibleContainerSmall, styles.cartItemSmallQuantity)}
        >
          <CartItemQuantityInput item={item} {...rest} />
        </div>
      </CartItemDetails>

      <CartItemPrice item={item} />

      <CartItemQuantity>
        <CartItemQuantityInput item={item} {...rest} />
      </CartItemQuantity>

      <CartItemSubTotal
        item={item}
        className={cx(cartContainerStyles.hiddenContainerSmall, mixinStyles.alignRight)}
      />
    </Fragment>
  );
};

export default CartItem;
